/**
 * Load More functionality for archive page
 * Fetches additional posts from Ghost Content API and appends them to the feed
 */

function initLoadMoreArchive() {
  const container = document.querySelector('.gh-load-more-container');
  if (!container) return; // Not on archive page or no more pages

  const button = document.getElementById('gh-load-more-btn');
  const errorDiv = document.getElementById('gh-load-more-error');
  const feed = document.getElementById('gh-archive-feed');
  const textSpan = button.querySelector('.gh-load-more-text');
  const spinnerSpan = button.querySelector('.gh-load-more-spinner');

  let currentPage = parseInt(container.dataset.currentPage, 10) || 1;
  const totalPages = parseInt(container.dataset.totalPages, 10) || 1;
  const siteUrl = container.dataset.siteUrl;
  const apiKey = container.dataset.apiKey;
  const postsPerPage = parseInt(container.dataset.postsPerPage, 10) || 15;
  let isLoading = false;

  // Validate API key is available
  if (!apiKey) {
    console.warn('Ghost Content API key not available. Load More functionality disabled.');
    container.style.display = 'none';
    return;
  }

  // Hide button if already on last page and show celebration message
  if (currentPage >= totalPages) {
    container.style.display = 'none';
    showCelebrationMessage();
    return;
  }

  /**
   * Format date to match Ghost's format
   */
  function formatDate(dateString) {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  /**
   * Calculate reading time (approximate)
   */
  function calculateReadingTime(html) {
    if (!html) return '1 min read';
    const text = html.replace(/<[^>]*>/g, '');
    const words = text.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  }

  /**
   * Generate image srcset for responsive images
   */
  function generateImageSrcset(imageUrl, sizes = ['xxs', 'xs', 's', 'm', 'l', 'xl']) {
    const widths = { xxs: 30, xs: 100, s: 300, m: 600, l: 1200, xl: 2000 };
    return sizes.map(size => {
      const width = widths[size];
      // Ghost's image service automatically resizes when you append ?w=WIDTH
      return `${imageUrl}?w=${width} ${width}w`;
    }).join(', ');
  }

  /**
   * Create a post card element from API data
   */
  function createPostCard(post) {
    const article = document.createElement('article');
    article.className = 'gh-card js-reveal';

    const link = document.createElement('a');
    link.className = 'gh-card-link';
    link.href = post.url;

    const content = document.createElement('div');
    content.className = 'gh-card-content';

    // Meta: date and reading time
    const meta = document.createElement('span');
    meta.className = 'gh-card-meta';
    const date = formatDate(post.published_at);
    const readingTime = calculateReadingTime(post.html);
    meta.innerHTML = `<time datetime="${post.published_at}">${date}</time> <span class="bull">&bull;</span> ${readingTime}`;

    // Title
    const title = document.createElement('h2');
    title.className = 'gh-card-title';
    title.textContent = post.title;

    // Excerpt
    const excerpt = document.createElement('p');
    excerpt.textContent = post.excerpt || '';

    // Read more
    const readMore = document.createElement('strong');
    readMore.textContent = 'Read more';

    content.appendChild(meta);
    content.appendChild(title);
    content.appendChild(excerpt);
    content.appendChild(readMore);
    link.appendChild(content);
    article.appendChild(link);

    return article;
  }

  /**
   * Fetch posts from Ghost Content API
   */
  async function fetchPosts(page) {
    const apiUrl = `${siteUrl}/ghost/api/content/posts/`;
    const params = new URLSearchParams({
      key: apiKey,
      limit: postsPerPage.toString(),
      page: page.toString(),
      fields: 'id,title,url,excerpt,html,published_at,feature_image,feature_image_alt',
      include: 'authors,tags'
    });

    try {
      const response = await fetch(`${apiUrl}?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching posts:', error);
      throw error;
    }
  }

  /**
   * Show loading state
   */
  function setLoadingState(loading) {
    isLoading = loading;
    button.disabled = loading;
    
    if (loading) {
      textSpan.style.display = 'none';
      spinnerSpan.style.display = 'inline-block';
      button.setAttribute('aria-label', 'Loading more posts...');
    } else {
      textSpan.style.display = 'inline';
      spinnerSpan.style.display = 'none';
      button.setAttribute('aria-label', 'Load more posts');
    }
  }

  /**
   * Show celebratory message when all posts are loaded
   */
  function showCelebrationMessage() {
    // Check if message already exists
    let celebrationDiv = document.getElementById('gh-load-more-celebration');
    
    if (!celebrationDiv) {
      // Create celebration message element
      celebrationDiv = document.createElement('div');
      celebrationDiv.id = 'gh-load-more-celebration';
      celebrationDiv.className = 'gh-load-more-celebration';
      celebrationDiv.setAttribute('role', 'status');
      celebrationDiv.setAttribute('aria-live', 'polite');
      celebrationDiv.textContent = "Congratulations! You've loaded all the posts! 🎉";
      
      // Insert after the feed
      feed.parentNode.insertBefore(celebrationDiv, feed.nextSibling);
    } else {
      // If it already exists, just make sure it's visible
      celebrationDiv.style.display = 'block';
    }
  }

  /**
   * Show error message
   */
  function showError(message) {
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
    errorDiv.setAttribute('aria-live', 'assertive');
    
    // Hide error after 5 seconds
    setTimeout(() => {
      errorDiv.style.display = 'none';
      errorDiv.textContent = '';
    }, 5000);
  }

  /**
   * Handle Load More button click
   */
  async function handleLoadMore() {
    if (isLoading) return;
    if (currentPage >= totalPages) {
      container.style.display = 'none';
      return;
    }

    const nextPage = currentPage + 1;
    setLoadingState(true);
    errorDiv.style.display = 'none';

    try {
      const data = await fetchPosts(nextPage);
      
      if (!data.posts || data.posts.length === 0) {
        // No more posts
        container.style.display = 'none';
        return;
      }

      // Append new posts to feed
      data.posts.forEach(post => {
        const card = createPostCard(post);
        feed.appendChild(card);
      });

      // Update current page
      currentPage = nextPage;

      // Hide button and show celebratory message if we've reached the last page
      if (currentPage >= totalPages || !data.meta?.pagination?.next) {
        container.style.display = 'none';
        showCelebrationMessage();
      }

      // Trigger scroll reveal animation for new cards
      // Re-observe new elements with scroll reveal
      const newCards = Array.from(feed.querySelectorAll('.gh-card.js-reveal')).slice(-data.posts.length);
      if (newCards.length > 0) {
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
          newCards.forEach(card => card.classList.add('reveal--visible'));
        } else if ('IntersectionObserver' in window) {
          const observer = new IntersectionObserver(
            (entries) => {
              entries.forEach(entry => {
                if (entry.isIntersecting) {
                  entry.target.classList.add('reveal--visible');
                  observer.unobserve(entry.target);
                }
              });
            },
            { threshold: 0.05 }
          );
          newCards.forEach(card => observer.observe(card));
        } else {
          newCards.forEach(card => card.classList.add('reveal--visible'));
        }
      }

    } catch (error) {
      console.error('Failed to load more posts:', error);
      showError('Failed to load more posts. Please try again.');
    } finally {
      setLoadingState(false);
    }
  }

  // Attach event listener
  button.addEventListener('click', handleLoadMore);

  // Keyboard accessibility
  button.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLoadMore();
    }
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLoadMoreArchive);
} else {
  initLoadMoreArchive();
}

export default initLoadMoreArchive;

